import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Stack, Chip, CircularProgress, Alert, Drawer, Divider,
    IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
    ToggleButton, ToggleButtonGroup, LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import AnalyticsService from '../services/analytics.service';
import { Panel, PanelHead } from './dashboard/Surface';
import { DASH } from './dashboard/dashboardTokens';

/**
 * Štatistiky používania appky — náhrada za Vexo.
 *
 * Zámerne bez heatmáp a bez videa: prvé sa nikdy nepoužilo, druhé by znamenalo
 * nahrávať obrazovky zamestnancov. Zostáva to, na čo sa Viktor naozaj pozerá —
 * koľko ľudí je v appke, ktoré obrazovky sa používajú a čo konkrétny človek
 * v relácii robil.
 *
 * Texty sú natvrdo po slovensky. Je to super-admin obrazovka pre jedného
 * človeka; prekladové kľúče do štyroch jazykov by tu boli réžia bez úžitku.
 */

const PERIODS = [
    { days: 1, label: '24 h' },
    { days: 7, label: '7 dní' },
    { days: 30, label: '30 dní' },
];

/** Sekundy na tvar, ktorý sa dá prečítať očami — „4 min 12 s", nie „252". */
const formatDuration = (seconds) => {
    const s = Number(seconds) || 0;
    if (s < 60) return `${s} s`;
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${m} min ${rest} s` : `${m} min`;
};

/**
 * Čas prichádza zo servera ako miestny reťazec bez zóny — zámerne, aby ho
 * prehliadač neposunul o dve hodiny. Preto sa parsuje bez `Z`.
 */
const formatMoment = (value) => (value ? dayjs(value).format('D. M. HH:mm') : '—');

const EVENT_LABELS = {
    app_open: 'Otvoril appku',
    screen: 'Prešiel na',
    foreground: 'Vrátil sa do appky',
    background: 'Odišiel z appky',
};

const Metric = ({ label, value, hint, accent }) => (
    <Panel sx={{ flex: 1, minWidth: 150 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography
            sx={{
                fontFamily: DASH.display,
                fontSize: 34,
                lineHeight: 1.1,
                mt: 0.5,
                ...DASH.numeric,
                ...(accent ? { color: 'success.main' } : null),
            }}
        >
            {value}
        </Typography>
        {hint ? (
            <Typography variant="caption" color="text.secondary">{hint}</Typography>
        ) : null}
    </Panel>
);

/** Mobil a web majú vlastné názvy obrazoviek — zmiešaný rebríček je nečitateľný. */
const PLATFORMS = [
    { value: null, label: 'Všetko' },
    { value: 'mobile', label: 'Mobil' },
    { value: 'web', label: 'Prehliadač' },
    { value: 'desktop', label: 'Desktop' },
];

const PLATFORM_LABELS = {
    ios: 'iOS',
    android: 'Android',
    web: 'Prehliadač',
    desktop: 'Desktop appka',
};

const AppAnalytics = () => {
    const [days, setDays] = useState(7);
    const [platform, setPlatform] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [overview, setOverview] = useState(null);
    const [screens, setScreens] = useState([]);
    const [users, setUsers] = useState([]);
    const [patterns, setPatterns] = useState([]);

    const [selectedUser, setSelectedUser] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [timeline, setTimeline] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [o, s, u, p] = await Promise.all([
                AnalyticsService.getOverview(),
                AnalyticsService.getScreens(days, platform),
                AnalyticsService.getUsers(days),
                AnalyticsService.getPatterns(days, platform),
            ]);
            setOverview(o.data);
            setScreens(s.data.screens || []);
            setUsers(u.data.users || []);
            setPatterns(p.data.patterns || []);
        } catch (e) {
            setError('Nepodarilo sa načítať štatistiky.');
        } finally {
            setLoading(false);
        }
    }, [days, platform]);

    useEffect(() => { load(); }, [load]);

    // „Práve teraz" starne rýchlo — bez obnovy by číslo na otvorenej stránke
    // ukazovalo stav spred hodiny.
    useEffect(() => {
        const id = setInterval(() => {
            AnalyticsService.getOverview()
                .then((r) => setOverview(r.data))
                .catch(() => {});
        }, 60000);
        return () => clearInterval(id);
    }, []);

    const openUser = async (user) => {
        setSelectedUser(user);
        setTimeline(null);
        setSessions([]);
        try {
            const r = await AnalyticsService.getSessions(days, user.employee_id);
            setSessions(r.data.sessions || []);
        } catch (e) {
            setSessions([]);
        }
    };

    const openSession = async (sessionId) => {
        try {
            const r = await AnalyticsService.getSessionTimeline(sessionId);
            setTimeline({ sessionId, events: r.data.events || [] });
        } catch (e) {
            setTimeline({ sessionId, events: [] });
        }
    };

    const maxOpens = screens.length ? Number(screens[0].opens) : 0;
    const totalPatternSessions = patterns.reduce((sum, p) => sum + Number(p.sessions), 0);

    if (loading && !overview) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: DASH.gap }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Typography sx={{ fontFamily: DASH.display, fontSize: 26 }}>
                    Používanie aplikácie
                </Typography>
                <Stack direction="row" gap={1.5} flexWrap="wrap">
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={platform}
                        onChange={(e, v) => setPlatform(v)}
                    >
                        {PLATFORMS.map((p) => (
                            <ToggleButton key={p.label} value={p.value}>{p.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={days}
                        onChange={(e, v) => v && setDays(v)}
                    >
                        {PERIODS.map((p) => (
                            <ToggleButton key={p.days} value={p.days}>{p.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}

            {overview ? (
                <Stack direction="row" flexWrap="wrap" gap={DASH.gap}>
                    <Metric
                        label="Práve v aplikácii"
                        value={overview.activeNow}
                        hint={`za posledných ${overview.active_now_minutes} min`}
                        accent={overview.activeNow > 0}
                    />
                    <Metric
                        label="Má aplikáciu"
                        value={overview.withApp}
                        hint={`z ${overview.employees} zamestnancov`}
                    />
                    <Metric label="Aktívnych za 24 h" value={overview.active24h} />
                    <Metric label="Aktívnych za 7 dní" value={overview.active7d} />
                    <Metric label="Aktívnych za 30 dní" value={overview.active30d} />
                    <Metric label="Relácií za 24 h" value={overview.sessions24h} />
                </Stack>
            ) : null}

            {overview && overview.platforms && overview.platforms.length ? (
                <Panel>
                    <PanelHead title="Odkiaľ chodia dáta" meta={`Za ${days} dní`} />
                    <Stack direction="row" gap={3} flexWrap="wrap">
                        {overview.platforms.map((p) => (
                            <Box key={p.platform}>
                                <Typography variant="caption" color="text.secondary">
                                    {PLATFORM_LABELS[p.platform] || p.platform}
                                </Typography>
                                <Typography sx={{ fontFamily: DASH.display, fontSize: 22, ...DASH.numeric }}>
                                    {p.sessions} relácií
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {p.events} udalostí · {p.people} ľudí
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Panel>
            ) : null}

            <Stack direction={{ xs: 'column', lg: 'row' }} gap={DASH.gap} alignItems="stretch">
                <Panel sx={{ flex: 1 }}>
                    <PanelHead title="Obrazovky" meta="Zoradené podľa počtu otvorení" />
                    {screens.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            Zatiaľ žiadne dáta — po vydaní OTA sa začnú zbierať.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Obrazovka</TableCell>
                                    <TableCell>Kde</TableCell>
                                    <TableCell align="right">Otvorení</TableCell>
                                    <TableCell align="right">Ľudí</TableCell>
                                    <TableCell align="right">Naposledy</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {screens.map((s) => (
                                    <TableRow key={s.screen} hover>
                                        <TableCell>
                                            <Typography variant="body2">{s.screen}</Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={maxOpens ? (Number(s.opens) / maxOpens) * 100 : 0}
                                                sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {String(s.platforms || '')
                                                    .split(',')
                                                    .map((x) => PLATFORM_LABELS[x] || x)
                                                    .join(', ')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={DASH.numeric}>{s.opens}</TableCell>
                                        <TableCell align="right" sx={DASH.numeric}>{s.people}</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption" color="text.secondary">
                                                {formatMoment(s.last_seen)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Panel>

                <Panel sx={{ flex: 1 }}>
                    <PanelHead title="Ako appku používajú" meta="Najčastejšie cesty v rámci jednej relácie" />
                    {patterns.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            Zatiaľ žiadne relácie.
                        </Typography>
                    ) : (
                        <Stack gap={1.5} sx={{ mt: 1 }}>
                            {patterns.slice(0, 12).map((p) => (
                                <Box key={p.path}>
                                    <Stack direction="row" justifyContent="space-between" gap={2}>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {p.path}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={
                                                totalPatternSessions
                                                    ? `${Math.round((Number(p.sessions) / totalPatternSessions) * 100)} %`
                                                    : p.sessions
                                            }
                                        />
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Panel>
            </Stack>

            <Panel>
                <PanelHead title="Ľudia" meta="Klikni na človeka a uvidíš jeho relácie" />
                {users.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Za toto obdobie nikto appku nepoužil.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Meno</TableCell>
                                <TableCell align="right">Relácií</TableCell>
                                <TableCell align="right">Udalostí</TableCell>
                                <TableCell align="right">Verzia</TableCell>
                                <TableCell align="right">Naposledy</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u) => (
                                <TableRow
                                    key={u.employee_id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => openUser(u)}
                                >
                                    <TableCell>{u.name || `#${u.employee_id}`}</TableCell>
                                    <TableCell align="right" sx={DASH.numeric}>{u.sessions}</TableCell>
                                    <TableCell align="right" sx={DASH.numeric}>{u.events}</TableCell>
                                    <TableCell align="right">
                                        <Typography variant="caption" color="text.secondary">
                                            {u.app_version || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="caption" color="text.secondary">
                                            {formatMoment(u.last_seen)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Panel>

            <Drawer
                anchor="right"
                open={Boolean(selectedUser)}
                onClose={() => { setSelectedUser(null); setTimeline(null); }}
                PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 3 } }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontFamily: DASH.display, fontSize: 20 }}>
                        {selectedUser?.name || ''}
                    </Typography>
                    <IconButton onClick={() => { setSelectedUser(null); setTimeline(null); }}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Divider sx={{ my: 2 }} />

                {timeline ? (
                    <>
                        <Typography
                            variant="body2"
                            sx={{ mb: 1, cursor: 'pointer' }}
                            color="primary"
                            onClick={() => setTimeline(null)}
                        >
                            ← späť na relácie
                        </Typography>
                        <Stack gap={1}>
                            {timeline.events.map((e, i) => (
                                <Stack
                                    key={`${e.occurred_at}-${i}`}
                                    direction="row"
                                    gap={1.5}
                                    alignItems="baseline"
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ ...DASH.numeric, minWidth: 54 }}
                                    >
                                        {String(e.occurred_at).slice(11, 19)}
                                    </Typography>
                                    <Typography variant="body2">
                                        {EVENT_LABELS[e.type] || e.type}
                                        {e.screen ? ` ${e.screen}` : ''}
                                    </Typography>
                                </Stack>
                            ))}
                            {timeline.events.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Relácia je prázdna.
                                </Typography>
                            ) : null}
                        </Stack>
                    </>
                ) : (
                    <Stack gap={1}>
                        {sessions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Za toto obdobie žiadne relácie.
                            </Typography>
                        ) : sessions.map((s) => (
                            <Panel
                                key={s.session_id}
                                sx={{ cursor: 'pointer', p: 2 }}
                                onClick={() => openSession(s.session_id)}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2">
                                            {formatMoment(s.started_at)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {s.screen_views} obrazoviek · {formatDuration(s.duration_seconds)}
                                        </Typography>
                                    </Box>
                                    <Tooltip title={s.platform || ''}>
                                        <Chip size="small" label={s.platform || '—'} />
                                    </Tooltip>
                                </Stack>
                            </Panel>
                        ))}
                    </Stack>
                )}
            </Drawer>
        </Box>
    );
};

export default AppAnalytics;
